import React, { useState, useEffect, useCallback } from "react";
import { Box, Stack, CircularProgress } from "@mui/material";
import { useParams, useNavigate } from "react-router";
import TopBar from "../../../../../component/TopBar";
import MainBox from "../../../container/MainBox";
import BtnSecondary from "../../../../../component/SecondaryButton";
import EmptyState from "../../../../../component/EmptyState";
import SectionLoader from "../../../../../component/SectionLoader";
import { getSelfPayslips } from "../../../../../services/payroll";
import { useNotify } from "../../../../../component/NotificationProvider";
import { tokens, displayFont, radius } from "../../../../../theme";
import { money, periodLabel } from "../../admin-main/payroll-new/payrollUtils";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
/** "29 August" — spelled from a table; ICU renders "Sept" for September. */
const dayMonth = (value) => {
  if (!value) return null;
  const s = String(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};

/** One line of the slip: name left, amount right, hairline under. */
const Line = ({ label, amount, negative }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="baseline"
    spacing={2}
    sx={{ py: "0.875rem", borderBottom: `1px solid ${tokens.border}` }}
  >
    <Box sx={{ fontSize: "0.9375rem", color: tokens.textMuted }}>{label}</Box>
    <Box sx={{ fontSize: "0.9375rem", color: tokens.text, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
      {negative ? `−${money(Math.abs(amount))}` : money(amount)}
    </Box>
  </Stack>
);

/**
 * The payslip, as the person who was paid reads it.
 *
 * "The in-app payslip is the one document allowed colour, and it leads with net
 * pay because that is the only figure most people came for. Nothing here is
 * editable, so nothing looks like a field."
 *
 * The admin's `PayslipDetail` is a different thing on purpose: a full statutory
 * sheet, laid out to be printed and filed. This one answers "how much, when,
 * and where did it go" and then gets out of the way.
 */
const PayslipDocument = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const [payslip, setPayslip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await getSelfPayslips();
      if (res?.success === false) throw new Error(res?.message || "Failed to load payslip");
      const found = (Array.isArray(res?.data) ? res.data : []).find((p) => String(p._id) === String(id));
      setPayslip(found || null);
      if (!found) setFailed(true);
    } catch (e) {
      setFailed(true);
      notify.error(e?.message || "Failed to load payslip");
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Box sx={{ width: "100%" }}>
        <TopBar MainHeading="Payslip" />
        <MainBox sx={{ padding: "1.25rem" }}><SectionLoader /></MainBox>
      </Box>
    );
  }

  if (failed || !payslip) {
    return (
      <Box sx={{ width: "100%" }}>
        <TopBar MainHeading="Payslip" />
        <MainBox sx={{ padding: "1.25rem" }}>
          <EmptyState variant="error" title="Couldn't load this payslip" onRetry={load} />
        </MainBox>
      </Box>
    );
  }

  const paidOn = dayMonth(payslip.payrollRun?.paymentDate || payslip.publishedAt);
  const isPaid = payslip.status === "paid" || Boolean(payslip.payrollRun?.paymentDate);

  /**
   * The line above the figure. It states what is true rather than assuming
   * payment: a generated payslip that has not been paid must not say it was,
   * and an account we do not know is left out rather than guessed at.
   */
  const header = [
    isPaid && paidOn ? `Paid ${paidOn}` : isPaid ? "Paid" : "Not yet paid",
    payslip.accountEnding ? `to account ending ${payslip.accountEnding}` : null,
  ].filter(Boolean).join(" ");

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar
        MainHeading="Payslip"
        breadcrumbs={[{ label: "Payslips", href: "/app/employee-payslips" }, { label: periodLabel(payslip.month, payslip.year) }]}
      />
      <MainBox sx={{ padding: "1.25rem" }}>
        <Box sx={{ maxWidth: "34rem", mx: "auto" }}>
          <Box sx={{ border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px`, overflow: "hidden" }}>
            {/*
              The one document allowed colour. Net pay first, because it is the
              only figure most people came for.
            */}
            <Box sx={{ background: tokens.sidebarBg, color: tokens.card, p: "1.5rem 1.625rem" }}>
              <Box sx={{ fontSize: "0.875rem", opacity: 0.85, mb: "0.5rem" }}>{header}</Box>
              <Box sx={{ fontFamily: displayFont, fontSize: "2.25rem", fontWeight: 700, lineHeight: 1.05, fontVariantNumeric: "tabular-nums" }}>
                {money(payslip.netPay)}
              </Box>
              <Box sx={{ fontSize: "0.875rem", opacity: 0.85, mt: "0.375rem" }}>
                Net pay · {periodLabel(payslip.month, payslip.year)}
              </Box>
            </Box>

            <Box sx={{ p: "0.5rem 1.625rem 1.625rem", background: tokens.card }}>
              <Line label="Gross" amount={payslip.grossPay} />
              {(payslip.deductions || []).map((d, i) => (
                <Line key={i} label={d.name} amount={d.amount} negative />
              ))}

              <Stack direction="row" spacing={1.5} sx={{ mt: "1.5rem" }}>
                {/* Two separate things: one saves a file, one sends it. */}
                <BtnSecondary onClick={() => window.print()} sx={{ borderRadius: "8px", flex: 1 }}>
                  Download PDF
                </BtnSecondary>
                <BtnSecondary
                  onClick={() => navigate(`/app/employee-payslips/${payslip._id}/full`)}
                  sx={{ borderRadius: "8px", flex: 1 }}
                >
                  Full breakdown
                </BtnSecondary>
              </Stack>
            </Box>
          </Box>

          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "1.125rem", textAlign: "center" }}>
            Questions about this payslip go to your payroll team.
          </Box>
        </Box>
      </MainBox>
    </Box>
  );
};

export default PayslipDocument;
